import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://2de0a4d9-b591-42a3-8794-fbf5b27a15e6.preview.emergentagent.com';
console.log('Using API base URL:', API_BASE_URL);

// Set up axios defaults
axios.defaults.baseURL = API_BASE_URL;

// Add request interceptor for debugging
axios.interceptors.request.use(request => {
  console.log('Making API request:', request.method?.toUpperCase(), request.url);
  return request;
});

// Add response interceptor for debugging
axios.interceptors.response.use(
  response => {
    console.log('API response:', response.status, response.data);
    return response;
  },
  error => {
    console.error('API error:', error.response?.status, error.response?.data || error.message);
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [teams, setTeams] = useState([]);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [skills, setSkills] = useState([]);
  const [helpRequests, setHelpRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('skills');
  const [isLoading, setIsLoading] = useState(true);

  // Auth forms
  const [showLogin, setShowLogin] = useState(true);
  const [authForm, setAuthForm] = useState({
    name: '',
    email: '',
    password: ''
  });

  // Team forms
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [teamForm, setTeamForm] = useState({
    name: '',
    description: '',
    inviteCode: ''
  });

  // Skill forms
  const [showAddSkill, setShowAddSkill] = useState(false);
  const [skillForm, setSkillForm] = useState({
    skill_name: '',
    rating: 3,
    category: 'Programming Languages'
  });

  // Help request forms
  const [showAddHelpRequest, setShowAddHelpRequest] = useState(false);
  const [helpRequestForm, setHelpRequestForm] = useState({
    skill_name: '',
    description: ''
  });

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  // Response form
  const [showResponseForm, setShowResponseForm] = useState(null);
  const [responseForm, setResponseForm] = useState({
    message: '',
    add_skill: false,
    skill_rating: 3
  });

  const skillCategories = [
    'Programming Languages',
    'Frameworks',
    'Tools',
    'Databases',
    'Cloud Platforms',
    'DevOps',
    'Testing',
    'Other'
  ];

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      loadUserData();
    }
  }, [user]);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await axios.get('/api/auth/me');
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
      }
    }
    setIsLoading(false);
  };

  const loadUserData = async () => {
    try {
      // Load teams
      const teamsResponse = await axios.get('/api/teams/my');
      setTeams(teamsResponse.data);
      
      // Set current team
      const currentTeamData = teamsResponse.data.find(t => t.id === user.current_team_id);
      setCurrentTeam(currentTeamData);

      if (user.current_team_id) {
        // Load skills
        const skillsResponse = await axios.get('/api/skills/my');
        setSkills(skillsResponse.data);

        // Load help requests
        const helpResponse = await axios.get('/api/help-requests');
        setHelpRequests(helpResponse.data);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    console.log('Form submission triggered', { showLogin, authForm });
    
    // Validate required fields
    if (!authForm.email || !authForm.password || (!showLogin && !authForm.name)) {
      console.log('Validation failed - missing required fields');
      alert('Please fill in all required fields');
      return;
    }
    
    try {
      const endpoint = showLogin ? '/api/auth/login' : '/api/auth/register';
      console.log('Making request to:', endpoint);
      
      const response = await axios.post(endpoint, authForm);
      console.log('Auth response:', response.data);
      
      localStorage.setItem('token', response.data.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      setUser(response.data.user);
      setAuthForm({ name: '', email: '', password: '' });
    } catch (error) {
      console.error('Auth error:', error);
      alert(error.response?.data?.detail || 'Authentication failed');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setTeams([]);
    setCurrentTeam(null);
    setSkills([]);
    setHelpRequests([]);
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    console.log('Team creation form submitted', teamForm);
    
    if (!teamForm.name.trim()) {
      console.log('Validation failed - team name is required');
      alert('Team name is required');
      return;
    }
    
    try {
      console.log('Making team creation request...');
      const response = await axios.post('/api/teams', {
        name: teamForm.name,
        description: teamForm.description
      });
      console.log('Team created successfully:', response.data);
      
      setShowCreateTeam(false);
      setTeamForm({ name: '', description: '', inviteCode: '' });
      await loadUserData();
    } catch (error) {
      console.error('Team creation failed:', error);
      alert(error.response?.data?.detail || 'Failed to create team');
    }
  };

  const handleJoinTeam = async (e) => {
    e.preventDefault();
    console.log('Join team form submitted', teamForm.inviteCode);
    
    if (!teamForm.inviteCode.trim()) {
      alert('Invite code is required');
      return;
    }
    
    try {
      console.log('Making join team request...');
      const response = await axios.post('/api/teams/join', {
        invite_code: teamForm.inviteCode
      });
      console.log('Team joined successfully:', response.data);
      
      setTeamForm({ name: '', description: '', inviteCode: '' });
      await loadUserData();
    } catch (error) {
      console.error('Join team failed:', error);
      alert(error.response?.data?.detail || 'Failed to join team');
    }
  };

  const handleSwitchTeam = async (teamId) => {
    try {
      await axios.post(`/api/teams/${teamId}/switch`);
      const updatedUser = { ...user, current_team_id: teamId };
      setUser(updatedUser);
      loadUserData();
    } catch (error) {
      alert('Failed to switch team');
    }
  };

  const handleAddSkill = async (e) => {
    e.preventDefault();
    console.log('Add skill form submitted', skillForm);
    
    try {
      console.log('Making add skill request...');
      const response = await axios.post('/api/skills', skillForm);
      console.log('Skill added successfully:', response.data);
      
      setShowAddSkill(false);
      setSkillForm({ skill_name: '', rating: 3, category: 'Programming Languages' });
      await loadUserData();
    } catch (error) {
      console.error('Add skill failed:', error);
      alert(error.response?.data?.detail || 'Failed to add skill');
    }
  };

  const handleDeleteSkill = async (skillId) => {
    try {
      await axios.delete(`/api/skills/${skillId}`);
      loadUserData();
    } catch (error) {
      alert('Failed to delete skill');
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const response = await axios.get(`/api/skills/search?skill_name=${searchQuery}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const handleAddHelpRequest = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/help-requests', helpRequestForm);
      setShowAddHelpRequest(false);
      setHelpRequestForm({ skill_name: '', description: '' });
      loadUserData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create help request');
    }
  };

  const handleRespondToRequest = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`/api/help-requests/${showResponseForm}/respond`, responseForm);
      setShowResponseForm(null);
      setResponseForm({ message: '', add_skill: false, skill_rating: 3 });
      loadUserData();
      alert('Response sent successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to send response');
    }
  };

  const getRatingStars = (rating) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  const getRatingText = (rating) => {
    const levels = { 1: 'Beginner', 2: 'Basic', 3: 'Intermediate', 4: 'Advanced', 5: 'Expert' };
    return levels[rating];
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Team Skills Hub</h1>
            <p className="text-gray-600">Connect, Share, and Learn Together</p>
            <button 
              onClick={async () => {
                console.log('Testing API connection...');
                try {
                  const response = await axios.get('/api/health');
                  console.log('Health check response:', response.data);
                  alert('API Connected! ' + JSON.stringify(response.data));
                } catch (error) {
                  console.error('API test failed:', error);
                  alert('API test failed: ' + error.message);
                }
              }}
              className="mt-2 text-xs bg-blue-500 text-white px-2 py-1 rounded"
            >
              Test API
            </button>
          </div>

          <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setShowLogin(true)}
              className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
                showLogin ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-600'
              }`}
            >
              Login
            </button>
            <button
              onClick={() => setShowLogin(false)}
              className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
                !showLogin ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-600'
              }`}
            >
              Register
            </button>
          </div>

          <form onSubmit={handleAuth} className="space-y-4">
            {!showLogin && (
              <input
                type="text"
                placeholder="Full Name"
                value={authForm.name}
                onChange={(e) => setAuthForm({ ...authForm, name: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
              />
            )}
            <input
              type="email"
              placeholder="Email"
              value={authForm.email}
              onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={authForm.password}
              onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            />
            <button
              type="submit"
              className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
            >
              {showLogin ? 'Login' : 'Register'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (!currentTeam) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-lg">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Welcome, {user.name}!</h2>
            <p className="text-gray-600">Join or create a team to get started</p>
          </div>

          {teams.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Your Teams</h3>
              <div className="space-y-2">
                {teams.map((team) => (
                  <div key={team.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{team.name}</p>
                      <p className="text-sm text-gray-600">{team.description}</p>
                    </div>
                    <button
                      onClick={() => handleSwitchTeam(team.id)}
                      className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      Select
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <button
              onClick={() => setShowCreateTeam(!showCreateTeam)}
              className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Create New Team
            </button>

            {showCreateTeam && (
              <form onSubmit={handleCreateTeam} className="space-y-3 p-4 bg-gray-50 rounded-lg">
                <input
                  type="text"
                  placeholder="Team Name"
                  value={teamForm.name}
                  onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                />
                <textarea
                  placeholder="Team Description"
                  value={teamForm.description}
                  onChange={(e) => setTeamForm({ ...teamForm, description: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  rows="3"
                />
                <button
                  type="submit"
                  className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Create Team
                </button>
              </form>
            )}

            <div className="text-center text-gray-500">or</div>

            <form onSubmit={handleJoinTeam} className="space-y-3">
              <input
                type="text"
                placeholder="Enter Invite Code"
                value={teamForm.inviteCode}
                onChange={(e) => setTeamForm({ ...teamForm, inviteCode: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
              <button
                type="submit"
                className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors"
              >
                Join Team
              </button>
            </form>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={handleLogout}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-bold text-gray-800">Team Skills Hub</h1>
              <div className="ml-6 flex items-center">
                <span className="text-sm text-gray-600">Team:</span>
                <span className="ml-2 px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-medium">
                  {currentTeam.name}
                </span>
                <span className="ml-2 text-xs text-gray-500">Code: {currentTeam.invite_code}</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Hello, {user.name}</span>
              <button
                onClick={handleLogout}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            {['skills', 'search', 'help'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab === 'skills' && 'My Skills'}
                {tab === 'search' && 'Find Experts'}
                {tab === 'help' && 'Help Requests'}
              </button>
            ))}
          </nav>
        </div>

        {/* Skills Tab */}
        {activeTab === 'skills' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">My Skills</h2>
              <button
                onClick={() => setShowAddSkill(true)}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Add Skill
              </button>
            </div>

            {skills.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">No skills added yet</p>
                <button
                  onClick={() => setShowAddSkill(true)}
                  className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Add Your First Skill
                </button>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {skills.map((skill) => (
                  <div key={skill.id} className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-lg font-semibold text-gray-800 capitalize">
                        {skill.skill_name}
                      </h3>
                      <button
                        onClick={() => handleDeleteSkill(skill.id)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        ×
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{skill.category}</p>
                    <div className="flex items-center">
                      <span className="text-yellow-400 text-lg">{getRatingStars(skill.rating)}</span>
                      <span className="ml-2 text-sm text-gray-600">
                        {getRatingText(skill.rating)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add Skill Modal */}
            {showAddSkill && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
                  <h3 className="text-lg font-semibold mb-4">Add New Skill</h3>
                  <form onSubmit={handleAddSkill} className="space-y-4">
                    <input
                      type="text"
                      placeholder="Skill Name (e.g., React, Python, Docker)"
                      value={skillForm.skill_name}
                      onChange={(e) => setSkillForm({ ...skillForm, skill_name: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                    <select
                      value={skillForm.category}
                      onChange={(e) => setSkillForm({ ...skillForm, category: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      {skillCategories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Skill Level: {getRatingText(skillForm.rating)}
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="5"
                        value={skillForm.rating}
                        onChange={(e) => setSkillForm({ ...skillForm, rating: parseInt(e.target.value) })}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>Beginner</span>
                        <span>Expert</span>
                      </div>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={() => setShowAddSkill(false)}
                        className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="flex-1 py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                      >
                        Add Skill
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Search Tab */}
        {activeTab === 'search' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Find Team Experts</h2>
              <div className="flex space-x-3">
                <input
                  type="text"
                  placeholder="Search for skills (e.g., React, Python, AWS)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <button
                  onClick={handleSearch}
                  className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Search
                </button>
              </div>
            </div>

            {searchResults.length > 0 && (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {searchResults.map((result, index) => (
                  <div key={index} className="bg-white p-6 rounded-lg shadow-sm border">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      {result.user_name}
                    </h3>
                    <p className="text-indigo-600 font-medium capitalize mb-2">
                      {result.skill_name}
                    </p>
                    <p className="text-sm text-gray-600 mb-3">{result.category}</p>
                    <div className="flex items-center">
                      <span className="text-yellow-400 text-lg">{getRatingStars(result.rating)}</span>
                      <span className="ml-2 text-sm text-gray-600">
                        {getRatingText(result.rating)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {searchQuery && searchResults.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">No experts found for "{searchQuery}"</p>
                <p className="text-sm text-gray-400 mt-2">Try searching for different skills or ask for help!</p>
              </div>
            )}
          </div>
        )}

        {/* Help Requests Tab */}
        {activeTab === 'help' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">Help Requests</h2>
              <button
                onClick={() => setShowAddHelpRequest(true)}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Request Help
              </button>
            </div>

            {helpRequests.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">No help requests yet</p>
                <button
                  onClick={() => setShowAddHelpRequest(true)}
                  className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Request Help
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {helpRequests.map((request) => (
                  <div key={request.id} className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800 capitalize">
                          Help needed with: {request.skill_name}
                        </h3>
                        <p className="text-sm text-gray-600">
                          Requested by: {request.requester_name}
                        </p>
                      </div>
                      {request.requester_id !== user.id && (
                        <button
                          onClick={() => setShowResponseForm(request.id)}
                          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                        >
                          Help Out
                        </button>
                      )}
                    </div>
                    <p className="text-gray-700 mb-3">{request.description}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(request.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Add Help Request Modal */}
            {showAddHelpRequest && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
                  <h3 className="text-lg font-semibold mb-4">Request Help</h3>
                  <form onSubmit={handleAddHelpRequest} className="space-y-4">
                    <input
                      type="text"
                      placeholder="Skill you need help with"
                      value={helpRequestForm.skill_name}
                      onChange={(e) => setHelpRequestForm({ ...helpRequestForm, skill_name: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                    <textarea
                      placeholder="Describe what you need help with..."
                      value={helpRequestForm.description}
                      onChange={(e) => setHelpRequestForm({ ...helpRequestForm, description: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      rows="4"
                      required
                    />
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={() => setShowAddHelpRequest(false)}
                        className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="flex-1 py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                      >
                        Request Help
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Response Modal */}
            {showResponseForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
                  <h3 className="text-lg font-semibold mb-4">Respond to Help Request</h3>
                  <form onSubmit={handleRespondToRequest} className="space-y-4">
                    <textarea
                      placeholder="Your response/offer to help..."
                      value={responseForm.message}
                      onChange={(e) => setResponseForm({ ...responseForm, message: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      rows="4"
                      required
                    />
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="add_skill"
                        checked={responseForm.add_skill}
                        onChange={(e) => setResponseForm({ ...responseForm, add_skill: e.target.checked })}
                        className="mr-2"
                      />
                      <label htmlFor="add_skill" className="text-sm text-gray-700">
                        Add this skill to my profile
                      </label>
                    </div>
                    {responseForm.add_skill && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Your skill level: {getRatingText(responseForm.skill_rating)}
                        </label>
                        <input
                          type="range"
                          min="1"
                          max="5"
                          value={responseForm.skill_rating}
                          onChange={(e) => setResponseForm({ ...responseForm, skill_rating: parseInt(e.target.value) })}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>Beginner</span>
                          <span>Expert</span>
                        </div>
                      </div>
                    )}
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={() => setShowResponseForm(null)}
                        className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="flex-1 py-2 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        Send Response
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default App;